// FILE: src/pages/ConfirmInvitePage.jsx
// Finish registration from the invitation email (MTO 1.1, 1.2.1), in three
// steps with Back / Continue:
//   1. a password that meets the policy (rules, strength and match shown live);
//   2. five different challenge questions from the server's ChallengeBank —
//      always, whether or not an authenticator app is used;
//   3. optionally an authenticator app, confirmed with a code before it is
//      switched on (A6, B-19), then a short review and "Complete registration".
// Continue checks only the step on screen. Nothing is sent until the final
// submit, except the authenticator set-up call when its box is ticked.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { checkInvite } from "../features/authSlice";
import { apiFetch } from "../services/api";
import { errorCode, errorMessage } from "../i18n/errors";
import { passwordFailures } from "../utils/passwordPolicy";
import PasswordRules from "../components/PasswordRules";
import PasswordStrength from "../components/PasswordStrength";
import PasswordField from "../components/PasswordField";
import ShowQRCode from "../components/ShowQRCode";
import PublicLayout from "../layouts/PublicLayout";
import { LoadingState } from "../components/ui";

const STEPS = [
  { id: "password", label: "Password", title: "Choose a password" },
  { id: "questions", label: "Security questions", title: "Answer five security questions" },
  { id: "review", label: "Sign-in options & review", title: "Sign-in options and review" }
];
const LAST = STEPS.length - 1;

const INVITE_UNCHECKED = { loading: false, error: null, valid: false, email: null };

const REQUIRED = 5;
// The server keeps an answer only when it has at least 2 characters after
// it drops accents, capitals and extra spaces (buildAnswerSet).
const MIN_ANSWER = 2;
const emptyAnswers = () => Array.from({ length: REQUIRED }, () => ({ questionId: "", answer: "" }));
const normalizedAnswer = (s) =>
  String(s || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** Field keys in screen order, per step: the first one with a problem gets focus. */
const FIELD_ORDER = [
  ["password", "confirm"],
  Array.from({ length: REQUIRED }, (_, i) => [`q${i}`, `a${i}`]).flat(),
  ["totp"]
];

/** Where a server refusal of the final submit belongs. */
function stepForServerError(err) {
  const code = errorCode(err);
  if (code === "WEAK_PASSWORD" || code === "PASSWORD_REUSED") return { step: 0, field: "password" };
  if (code === "CHALLENGE_ANSWERS_REQUIRED") return { step: 1, field: "q0" };
  if (["INVALID_2FA_TOKEN", "INVALID_TOTP", "TWOFA_TOKEN_REQUIRED", "TOTP_RATE_LIMIT", "TWOFA_SETUP_NOT_STARTED"].includes(code)) {
    return { step: 2, field: "totp" };
  }
  if (code === "VALIDATION_FAILED") {
    const fields = (err?.problems || []).map((p) => p.field);
    if (fields.includes("password")) return { step: 0, field: "password" };
    if (fields.includes("totpCode") || fields.includes("enable2FA")) return { step: 2, field: "totp" };
  }
  return { step: LAST, field: "serverError" };
}

async function postJson(path, body) {
  try {
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    return { ok: res.ok, data: await res.json().catch(() => ({})) };
  } catch {
    return { ok: false, data: { code: "NETWORK" } };
  }
}

function Shell({ children }) {
  return (
    <PublicLayout maxWidth={680}>
      <Card sx={{ borderRadius: 4, my: 2 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>{children}</CardContent>
      </Card>
    </PublicLayout>
  );
}

const TINTS = {
  success: { bgcolor: "zun.primaryTint", color: "primary.main" },
  info: { bgcolor: "zun.infoTint", color: "info.main" },
  warning: { bgcolor: "zun.warningTint", color: "warning.main" },
  error: { bgcolor: "zun.errorTint", color: "error.main" }
};

/** A whole-page state (invitation problem, success): icon + h1 + words + next action. */
function StateView({ icon: Icon, tone, title, children, actions, headingRef, testId }) {
  return (
    <Shell>
      <Box
        aria-hidden
        sx={{ width: 52, height: 52, borderRadius: 3, display: "grid", placeItems: "center", mb: 2, ...TINTS[tone] }}
      >
        <Icon />
      </Box>
      <Typography variant="h1" sx={{ mb: 1, "&:focus": { outline: "none" } }} tabIndex={-1} ref={headingRef}>
        {title}
      </Typography>
      <Box sx={{ color: "text.secondary", mb: 3 }} data-testid={testId}>
        {children}
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        {actions}
      </Stack>
    </Shell>
  );
}

/** What went wrong with the invitation link, in words, and what to do next. */
function InviteProblem({ code, onRetry }) {
  const signIn = (variant) => (
    <Button component={RouterLink} to="/login" variant={variant} size="large">
      Go to sign in
    </Button>
  );
  const contact = (
    <Button component={RouterLink} to="/contact" variant="contained" size="large">
      Contact student support
    </Button>
  );
  if (code === "INVITE_NOT_PENDING") {
    return (
      <StateView icon={TaskAltIcon} tone="info" title="This invitation has already been used" actions={signIn("contained")} testId="invite-problem">
        <Typography>Your account is already set up. Sign in with your email address and the password you chose.</Typography>
      </StateView>
    );
  }
  if (code === "EXPIRED_TOKEN") {
    return (
      <StateView
        icon={EventBusyOutlinedIcon}
        tone="warning"
        title="This invitation has expired"
        testId="invite-problem"
        actions={
          <>
            {contact}
            {signIn("outlined")}
          </>
        }
      >
        <Typography>{errorMessage(code)} Student support can send you a new invitation.</Typography>
      </StateView>
    );
  }
  if (code === "NETWORK" || code === "INTERNAL_ERROR") {
    return (
      <StateView
        icon={CloudOffOutlinedIcon}
        tone="warning"
        title="We couldn't check your invitation"
        testId="invite-problem"
        actions={
          <>
            <Button variant="contained" size="large" onClick={onRetry}>
              Try again
            </Button>
            {signIn("outlined")}
          </>
        }
      >
        <Typography>{errorMessage(code)}</Typography>
      </StateView>
    );
  }
  // The server forgets the token once it's used, so a used link lands here too.
  const incomplete = code === "TOKEN_NOT_FOUND";
  return (
    <StateView
      icon={LinkOffIcon}
      tone="error"
      title={incomplete ? "This invitation link is incomplete" : "This invitation link isn't valid"}
      testId="invite-problem"
      actions={
        <>
          {signIn("contained")}
          {React.cloneElement(contact, { variant: "outlined" })}
        </>
      }
    >
      <Typography sx={{ mb: 1 }}>{errorMessage(code || "INVALID_TOKEN")}</Typography>
      <Typography>
        If you've already set up your account with it, sign in with your email address and password. If not, contact student
        support for a new invitation.
      </Typography>
    </StateView>
  );
}

/** One line of the review (a dt/dd pair): label, status icon + words, optional change button. */
function ReviewRow({ label, ok, okIcon: OkIcon = CheckCircleIcon, tone, children, action }) {
  const Icon = ok ? OkIcon : WarningAmberOutlinedIcon;
  return (
    <Box sx={{ py: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
      <Typography component="dt" sx={{ color: "text.secondary", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography component="dd" sx={{ m: 0, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", overflowWrap: "anywhere" }}>
        <Icon aria-hidden fontSize="small" sx={{ color: tone || (ok ? "primary.main" : "warning.main") }} />
        <Box component="span" sx={{ flex: "1 1 160px", minWidth: 0 }}>
          {children}
        </Box>
        {action}
      </Typography>
    </Box>
  );
}

export default function ConfirmInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const dispatch = useDispatch();
  // After a reload redux-persist restores `auth` as only { user, role }, so
  // invitationCheck can be missing until the check below runs.
  const { loading: inviteLoading, error: inviteError, valid, email } =
    useSelector((s) => s.auth.invitationCheck) || INVITE_UNCHECKED;

  const [checkedToken, setCheckedToken] = useState(null);
  const [bank, setBank] = useState([]);
  const [bankError, setBankError] = useState(null);
  const [step, setStep] = useState(0);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [answers, setAnswers] = useState(emptyAnswers);
  const [useTotp, setUseTotp] = useState(false);
  const [totp, setTotp] = useState(null); // { otpauthUrl, secret }
  const [totpBusy, setTotpBusy] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null); // { error, step }
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { twoFactorEnabled }
  const [focusTarget, setFocusTarget] = useState(null);

  const fields = useRef({});
  const headings = useRef([]);
  const doneHeading = useRef(null);
  const field = (key) => (el) => {
    if (el) fields.current[key] = el;
  };

  function runCheck() {
    if (!token) return;
    Promise.resolve(dispatch(checkInvite(token))).finally(() => setCheckedToken(token));
  }

  useEffect(() => {
    runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, token]);

  function loadBank() {
    setBankError(null);
    apiFetch("/api/auth/challenge-questions")
      .then((r) => (r.ok ? r.json() : Promise.reject({ code: "INTERNAL_ERROR" })))
      .then((d) => setBank(d.questions || []))
      .catch((err) => {
        setBank([]);
        setBankError(errorCode(err) || "NETWORK");
      });
  }

  useEffect(loadBank, []);

  // Move focus after the step on screen has changed (screen readers then
  // read the new step's heading, or the field that needs fixing).
  useEffect(() => {
    if (!focusTarget) return;
    const el =
      focusTarget === "heading" ? headings.current[step] : focusTarget === "done" ? doneHeading.current : fields.current[focusTarget];
    if (el && typeof el.focus === "function") el.focus();
    setFocusTarget(null);
  }, [focusTarget, step, done]);

  /** Which question each question id is used for: id → index. */
  const usedBy = useMemo(() => {
    const m = new Map();
    answers.forEach((a, i) => {
      if (a.questionId && !m.has(a.questionId)) m.set(a.questionId, i);
    });
    return m;
  }, [answers]);

  const setAnswer = (i, patch) => {
    setAnswers((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } : a)));
    setErrors((e) => ({ ...e, [`q${i}`]: null, [`a${i}`]: null }));
  };

  const passwordOk = passwordFailures(password).length === 0;
  const answeredCount = answers.filter(
    (a, i) => a.questionId && usedBy.get(a.questionId) === i && normalizedAnswer(a.answer).length >= MIN_ANSWER
  ).length;

  async function toggleTotp(checked) {
    if (totpBusy) return;
    setUseTotp(checked);
    setServerError(null);
    setErrors((e) => ({ ...e, totp: null }));
    if (checked && !totp) {
      setTotpBusy(true);
      const { ok, data } = await postJson("/api/auth/confirm-invite/totp", { token });
      setTotpBusy(false);
      if (ok) setTotp(data);
      else {
        setUseTotp(false);
        setServerError({ error: data.code ? data : { code: "INVALID_TOKEN" }, step: LAST });
      }
    }
  }

  /** Problems on one step only: { fieldKey: message }. */
  function stepErrors(n) {
    const e = {};
    if (n === 0) {
      if (!passwordOk) e.password = "Your password doesn't meet all the rules yet.";
      if (!confirm) e.confirm = "Type your password again to confirm it.";
      else if (confirm !== password) e.confirm = "The two passwords don't match.";
    }
    if (n === 1) {
      answers.forEach((a, i) => {
        if (!a.questionId) e[`q${i}`] = "Choose a question.";
        else if (usedBy.get(a.questionId) !== i) {
          e[`q${i}`] = `Already used for question ${usedBy.get(a.questionId) + 1}. Choose a different one.`;
        }
        if (normalizedAnswer(a.answer).length < MIN_ANSWER) e[`a${i}`] = `Answer with at least ${MIN_ANSWER} characters.`;
      });
    }
    if (n === 2 && useTotp) {
      if (!totp) e.totp = "The authenticator app isn't set up yet. Untick the box and tick it again, or finish without it.";
      else if (!/^\d{6}$/.test(totpCode.trim())) e.totp = "Enter the 6-digit code your app shows.";
    }
    return e;
  }

  /** Show a step's problems and focus the first one; false when there are none. */
  function flag(n, e) {
    if (!Object.keys(e).length) return false;
    setErrors(e);
    setStep(n);
    const first = FIELD_ORDER[n].find((k) => e[k]) || "heading";
    setFocusTarget(first);
    return true;
  }

  function goTo(n) {
    setServerError(null);
    setErrors({});
    setStep(n);
    setFocusTarget("heading");
  }

  function next() {
    setServerError(null);
    if (flag(step, stepErrors(step))) return;
    goTo(step + 1);
  }

  async function submit(ev) {
    ev.preventDefault();
    if (step < LAST) {
      next();
      return;
    }
    setServerError(null);
    for (let n = 0; n <= LAST; n++) if (flag(n, stepErrors(n))) return;
    setSubmitting(true);
    const { ok, data } = await postJson("/api/auth/confirm-invite", {
      token,
      password,
      challengeAnswers: answers.map((a) => ({ questionId: a.questionId, answer: a.answer.trim() })),
      enable2FA: useTotp,
      ...(useTotp ? { totpCode: totpCode.trim() } : {})
    });
    setSubmitting(false);
    if (ok) {
      setDone({ twoFactorEnabled: data.twoFactorEnabled ?? useTotp });
      setFocusTarget("done");
      return;
    }
    const error = data && data.code ? data : { code: "VALIDATION_FAILED" };
    const where = stepForServerError(error);
    setErrors({});
    setServerError({ error, step: where.step });
    setStep(where.step);
    setFocusTarget(where.field);
  }

  if (!token) return <InviteProblem code="TOKEN_NOT_FOUND" />;
  if (inviteLoading || checkedToken !== token) {
    return (
      <Shell>
        <LoadingState variant="inline" label="Checking your invitation…" />
      </Shell>
    );
  }
  if (inviteError || !valid) return <InviteProblem code={errorCode(inviteError) || "INVALID_TOKEN"} onRetry={runCheck} />;

  if (done) {
    return (
      <StateView
        icon={CheckCircleOutlineIcon}
        tone="success"
        title="You're registered"
        headingRef={doneHeading}
        testId="registration-done"
        actions={
          <Button component={RouterLink} to="/login" variant="contained" size="large" fullWidth>
            Sign in
          </Button>
        }
      >
        <Typography role="status">
          Your account is ready. Each time you sign in we'll email you a one-time code
          {done.twoFactorEnabled ? " and ask for the code from your authenticator app." : "."}
        </Typography>
      </StateView>
    );
  }

  const serverAlert = (n) =>
    serverError && serverError.step === n ? (
      <Alert severity="error" sx={{ mb: 2 }} role="alert" tabIndex={-1} ref={n === LAST ? field("serverError") : undefined}>
        {errorMessage(serverError.error)}
        {errorCode(serverError.error) === "INVITE_NOT_PENDING" && (
          <>
            {" "}
            <Link component={RouterLink} to="/login">
              Go to sign in
            </Link>
          </>
        )}
      </Alert>
    ) : null;

  const heading = (n, intro) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="h2"
        tabIndex={-1}
        ref={(el) => {
          headings.current[n] = el;
        }}
        sx={{ scrollMarginTop: 16 }}
      >
        <Box component="span" sx={{ display: "block", color: "text.secondary", fontSize: "1rem", fontWeight: 600, mb: 0.5 }}>
          {`Step ${n + 1} of ${STEPS.length}`}
        </Box>{" "}
        {STEPS[n].title}
      </Typography>
      {intro && <Typography sx={{ color: "text.secondary", mt: 1 }}>{intro}</Typography>}
    </Box>
  );

  // Live "match" line under Confirm password, once something is typed there.
  const same = confirm === password;
  const confirmStatus = errors.confirm
    ? { error: true, text: errors.confirm }
    : {
        error: false,
        text: (
          <Box component="span" sx={{ display: "inline-flex", gap: 0.75, alignItems: "center" }}>
            {confirm &&
              (same ? (
                <CheckCircleIcon aria-hidden fontSize="small" sx={{ color: "primary.main" }} />
              ) : (
                <CancelOutlinedIcon aria-hidden fontSize="small" sx={{ color: "text.secondary" }} />
              ))}
            <span aria-live="polite" data-testid="password-match">
              {!confirm ? "\u00a0" : same ? "Passwords match" : "Passwords don't match yet"}
            </span>
          </Box>
        )
      };

  return (
    <Shell>
      <Typography variant="h1" sx={{ mb: 1 }}>
        Set up your account
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }} data-testid="invite-email">
        You're setting up the account for{" "}
        <Box component="strong" sx={{ color: "text.primary", overflowWrap: "anywhere" }}>
          {email}
        </Box>
        . It takes about 3 minutes.
      </Typography>

      <Stepper
        activeStep={step}
        alternativeLabel
        component="ol"
        aria-label="Registration steps"
        sx={{ display: { xs: "none", sm: "flex" }, listStyle: "none", p: 0, m: 0, mb: 4 }}
      >
        {STEPS.map((s, i) => (
          <Step key={s.id} component="li" completed={i < step} aria-current={i === step ? "step" : undefined}>
            <StepLabel
              optional={
                i < step ? (
                  <Typography component="span" sx={{ display: "block", color: "text.secondary" }}>
                    Done
                  </Typography>
                ) : null
              }
            >{s.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box component="form" onSubmit={submit} noValidate>
        {/* Lets password managers save the new password with the right email. */}
        <input type="email" name="email" autoComplete="username" value={email || ""} readOnly hidden />

        <Box hidden={step !== 0} data-testid="step-password">
          {heading(0, "You'll sign in with your email address and this password.")}
          {serverAlert(0)}
          <Stack spacing={1}>
            <PasswordField
              id="new-password"
              label="New password"
              required
              fullWidth
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((x) => ({ ...x, password: null, confirm: null }));
              }}
              error={!!errors.password}
              helperText={errors.password}
              inputRef={field("password")}
              toggleLabel="Show password"
              slotProps={{
                htmlInput: {
                  "aria-describedby": [errors.password && "new-password-helper-text", "password-strength-status", "password-rules"]
                    .filter(Boolean)
                    .join(" ")
                }
              }}
            />
            <PasswordStrength password={password} />
            <PasswordRules password={password} />
            <PasswordField
              id="confirm-password"
              label="Confirm password"
              required
              fullWidth
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                setErrors((x) => ({ ...x, confirm: null }));
              }}
              error={confirmStatus.error}
              helperText={confirmStatus.text}
              inputRef={field("confirm")}
              toggleLabel="Show password confirmation"
            />
          </Stack>
        </Box>

        <Box hidden={step !== 1} data-testid="step-questions">
          {heading(
            1,
            "During the course we'll ask you one of these at random to confirm it's really you. Choose five different questions (each one can be used only once) and answers you'll remember. Capitals and extra spaces don't matter."
          )}
          {serverAlert(1)}
          {bankError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              role="alert"
              action={
                <Button color="inherit" onClick={loadBank}>
                  Try again
                </Button>
              }
            >
              We couldn't load the security questions. {errorMessage(bankError)}
            </Alert>
          )}
          {answers.map((a, i) => {
            const usedElsewhere = a.questionId && usedBy.get(a.questionId) !== i;
            const qError = errors[`q${i}`] || (usedElsewhere ? `Already used for question ${usedBy.get(a.questionId) + 1}. Choose a different one.` : null);
            const len = normalizedAnswer(a.answer).length;
            const aError = errors[`a${i}`];
            const aHint = !aError && a.answer && len < MIN_ANSWER ? `Use at least ${MIN_ANSWER} characters.` : null;
            return (
              <Stack key={i} direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
                <FormControl fullWidth required error={!!qError} sx={{ flex: 3, minWidth: 0 }}>
                  <InputLabel id={`q${i}-label`}>{`Question ${i + 1}`}</InputLabel>
                  <Select
                    labelId={`q${i}-label`}
                    label={`Question ${i + 1}`}
                    value={a.questionId}
                    onChange={(e) => setAnswer(i, { questionId: e.target.value })}
                    inputRef={field(`q${i}`)}
                    renderValue={(id) => bank.find((q) => q.id === id)?.text || ""}
                    sx={{ "& .MuiSelect-select": { whiteSpace: "normal" } }}
                    aria-describedby={qError ? `q${i}-helper` : undefined}
                    data-testid={`question-${i}`}
                  >
                    {bank.map((q) => {
                      const owner = usedBy.get(q.id);
                      const taken = owner !== undefined && owner !== i;
                      return (
                        <MenuItem key={q.id} value={q.id} disabled={taken} sx={{ whiteSpace: "normal", display: "block" }}>
                          {q.text}
                          {taken && (
                            <Box component="span" sx={{ display: "block", color: "text.secondary" }}>
                              {`Already used for question ${owner + 1}`}
                            </Box>
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {qError && <FormHelperText id={`q${i}-helper`}>{qError}</FormHelperText>}
                </FormControl>
                <TextField
                  label={`Answer ${i + 1}`}
                  required
                  value={a.answer}
                  onChange={(e) => setAnswer(i, { answer: e.target.value })}
                  error={!!aError}
                  helperText={aError || aHint || " "}
                  autoComplete="off"
                  inputRef={field(`a${i}`)}
                  sx={{ flex: 2 }}
                />
              </Stack>
            );
          })}
        </Box>

        <Box hidden={step !== 2} data-testid="step-review">
          {heading(2)}
          {serverAlert(2)}
          <Typography variant="h3" sx={{ mb: 1 }}>
            Authenticator app (optional)
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 1 }}>
            Every sign-in already uses a code we email you. You can add an authenticator app as an extra lock, now or later from your
            profile.
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={useTotp} onChange={(e) => toggleTotp(e.target.checked)} />}
            label="Also ask for a code from an authenticator app when I sign in"
          />
          {useTotp && totpBusy && <LoadingState variant="inline" label="Setting up the authenticator app…" />}
          {useTotp && totp && (
            <Stack spacing={1.5} sx={{ mt: 1, p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
              <Typography>
                Scan this code with an app such as Google Authenticator or Microsoft Authenticator, then type the 6-digit code it
                shows.
              </Typography>
              <ShowQRCode otpauthURL={totp.otpauthUrl} />
              <Typography>
                Can't scan? Enter this key in the app:{" "}
                <Box component="code" sx={{ overflowWrap: "anywhere" }}>
                  {totp.secret}
                </Box>
              </Typography>
              <TextField
                label="Code from the app"
                required
                value={totpCode}
                onChange={(e) => {
                  setTotpCode(e.target.value);
                  setErrors((x) => ({ ...x, totp: null }));
                }}
                autoComplete="one-time-code"
                inputRef={field("totp")}
                slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 6, pattern: "[0-9]*" } }}
                error={!!errors.totp}
                helperText={errors.totp || " "}
              />
            </Stack>
          )}
          {useTotp && !totp && !totpBusy && errors.totp && (
            <Alert severity="error" sx={{ mt: 1 }} tabIndex={-1} ref={field("totp")}>
              {errors.totp}
            </Alert>
          )}

          <Typography variant="h3" sx={{ mt: 4, mb: 1 }}>
            Check your details
          </Typography>
          <Box component="dl" sx={{ m: 0, borderBottom: "1px solid", borderColor: "divider" }} data-testid="review">
            <ReviewRow label="Email address" ok okIcon={CheckCircleOutlineIcon}>
              {email}
            </ReviewRow>
            <ReviewRow
              label="Password"
              ok={passwordOk && confirm === password}
              action={
                <Button type="button" onClick={() => goTo(0)}>
                  Change password
                </Button>
              }
            >
              {passwordOk && confirm === password ? "Set: it meets every rule" : "Not finished yet"}
            </ReviewRow>
            <ReviewRow
              label="Security questions"
              ok={answeredCount === REQUIRED}
              action={
                <Button type="button" onClick={() => goTo(1)}>
                  Change questions
                </Button>
              }
            >
              {answeredCount === REQUIRED ? `${REQUIRED} different questions answered` : `${answeredCount} of ${REQUIRED} answered`}
            </ReviewRow>
            <ReviewRow
              label="Authenticator app"
              ok
              okIcon={useTotp ? CheckCircleIcon : RemoveCircleOutlineIcon}
              tone={useTotp ? "primary.main" : "text.secondary"}
            >
              {useTotp ? "On: you'll also enter its code when you sign in" : "Off: you can turn it on later from your profile"}
            </ReviewRow>
          </Box>
        </Box>

        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ mt: 4, justifyContent: "space-between" }}>
          {step > 0 ? (
            <Button type="button" variant="outlined" size="large" startIcon={<ArrowBackIcon aria-hidden />} onClick={() => goTo(step - 1)} disabled={submitting}>
              Back
            </Button>
          ) : (
            <Box aria-hidden sx={{ display: { xs: "none", sm: "block" } }} />
          )}
          {step < LAST ? (
            <Button type="submit" variant="contained" size="large" endIcon={<ArrowForwardIcon aria-hidden />}>
              Continue
            </Button>
          ) : (
            <Button type="submit" variant="contained" size="large" disabled={submitting} sx={{ minWidth: { sm: 260 } }}>
              {submitting ? <CircularProgress size={24} color="inherit" aria-label="Saving" /> : "Complete registration"}
            </Button>
          )}
        </Stack>
      </Box>

      <Typography sx={{ color: "text.secondary", mt: 3 }}>
        Already set up?{" "}
        <Link component={RouterLink} to="/login">
          Sign in
        </Link>
      </Typography>
    </Shell>
  );
}
